import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useKey } from './useKey';

export type TType = {
  id: string | number;
};

export interface ISearchContext<TResult, TSuggestion> {
  searchInput: string;
  results?: TResult[] | null;
  suggestions?: TSuggestion[] | null;
  noResults: boolean;
  isLoading: boolean;
  focusIndex: number | null;
  focusedItem: TResult | TSuggestion | null;
  layoutMode: 'mobile' | 'desktop';
  error?: 'string' | React.ReactNode;
  setFocusIndex(index: number): void;
  gotoNextFocus(): void;
  gotoPrevFocus(): void;
  setLayoutMode(mode: 'mobile' | 'desktop'): void;
  onSelectSearchResult?(result: TResult): void;
  onSelectSearchSuggestion?(result: TSuggestion): void;
}

export const SearchContext = React.createContext<ISearchContext<any, any> | undefined>(undefined);

export function SearchContextProvider<TResult extends TType | null, TSuggestion extends TType | null>({
  children,
  searchInput,
  results,
  suggestions,
  isLoading,
  isOpen,
  noResults,
  error,
  onSelectSearchResult,
  onSelectSearchSuggestion
}: {
  children: React.ReactNode;
  searchInput: string;
  results: TResult[] | null;
  suggestions: TSuggestion[] | null;
  isLoading: boolean;
  isOpen: boolean;
  noResults: boolean;
  error?: 'string' | React.ReactNode;
  onSelectSearchResult?(result: TResult): void;
  onSelectSearchSuggestion?(result: TSuggestion): void;
}) {
  // Tracking whether we are currently showing suggestions or results.
  const showingResults = results && results.length > 0 && searchInput.length !== 0;

  const searchInputRef = useRef(searchInput);
  // ==== Focus Management ====
  // Focus state is kb navigable. Up/Down keys will change the focus index, as will hover.
  // This starts at -1 so that pressing down iterates it to the first item in the index
  const [focusIndex, setFocusIndex] = React.useState<number>(-1);
  // focusMax prevents kb nav beyond the last item in the list
  const sugMax = suggestions ? suggestions.length - 1 : 0;
  const focusMax = showingResults ? results.length - 1 : sugMax;
  // increment callbacks passed to children as callbacks for perf reasons
  const gotoNextFocus = useCallback(() => setFocusIndex((x) => Math.min(focusMax, x + 1)), [focusMax]);
  const gotoPrevFocus = useCallback(() => setFocusIndex((x) => Math.max(0, x - 1)), []);

  useEffect(() => {
    // Don't reset focus index if consumer passes in new results when search input is empty.
    if (!(searchInputRef.current === searchInput && searchInput === '')) {
      setFocusIndex(-1);
    }
    searchInputRef.current = searchInput;
  }, [results, searchInput]);

  // ==== Preview Management ====
  // focusedItem is used to show a preview panel of the currently focused item
  const sugItem = suggestions ? suggestions[focusIndex] : null;
  const focusedItem = showingResults ? results[focusIndex] : sugItem;

  // ==== Keyboard Navigation ====
  const selectFocusedItem = useCallback(() => {
    if (!focusedItem) return;
    if (showingResults) {
      onSelectSearchResult?.(focusedItem as TResult);
    } else {
      onSelectSearchSuggestion?.(focusedItem as TSuggestion);
    }
  }, [showingResults, focusedItem, onSelectSearchResult, onSelectSearchSuggestion]);
  useKey('ArrowDown', () => {
    if (!isOpen) return;
    gotoNextFocus();
  });
  useKey('ArrowUp', () => {
    if (!isOpen) return;
    gotoPrevFocus();
  });
  useKey('Enter', (event: KeyboardEvent) => {
    if (!isOpen) return;
    selectFocusedItem();
    // Closing dialog via enter callbacks is triggering reopen of dialog.
    // see https://github.com/mui/material-ui/issues/13770 for more discussion.
    // Another option is to setTimeout(() => setOpen(false), 0) in selection
    // callbacks but that requires consumers to institute a hack.
    event.preventDefault();
  });

  // ==== Layout Management ====
  // layoutMode is used to determine whether to show the preview panel on mobile
  const [layoutMode, setLayoutMode] = React.useState<'mobile' | 'desktop'>('desktop');

  const value = useMemo(
    () => ({
      searchInput,
      results,
      suggestions,
      noResults,
      isLoading,
      focusIndex,
      setFocusIndex,
      gotoNextFocus,
      gotoPrevFocus,
      focusedItem,
      layoutMode,
      setLayoutMode,
      error,
      onSelectSearchResult,
      onSelectSearchSuggestion
    }),
    [
      searchInput,
      results,
      suggestions,
      noResults,
      isLoading,
      focusIndex,
      setFocusIndex,
      gotoNextFocus,
      gotoPrevFocus,
      focusedItem,
      layoutMode,
      setLayoutMode,
      error,
      onSelectSearchResult,
      onSelectSearchSuggestion
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}
